import { listIssues, updateIssue } from '../db.js';
import { escapeHtml } from '../ui.js';

// Reported issues — everything sent through "Report an issue". Benchmark fold
// (HANDOFF §8): SEEING open issues is now open to the whole team, so anyone can
// tell whether the thing annoying them is already known; RESOLVING stays with
// Owner and Director. v4.0.0: its own page again, Help / Issues (p40), linked
// from Help's "Behind the scenes".
//
// Reporter identity, the browser string and the screenshot are shown only to
// resolvers — the rest of the team sees the message, the screen and the status.

const canResolve = (session) => !!(session && session.roles
  && ['Owner', 'Director'].some((r) => session.roles.includes(r)));

const CHECK = '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.5"/><path d="M8 12.5l2.7 2.7L16 9.8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
const report = () => import('../telemetry.js').then((m) => m.openReportDialog()).catch(() => {});

/**
 * ISSUES — v4.0.0 refresh (p40). Help / Issues: Open | Resolved with live
 * counts, hairline rows in the Releases grammar, and an empty state that says
 * what lands here. Seeing is open to all; resolving is Owner and Director.
 */
export async function renderIssues(mount, session, { onGo } = {}) {
  mount.innerHTML = `
    <div class="is4">
      <nav class="crumbs" aria-label="Breadcrumb"><button type="button" data-crumb>Help</button><span>/</span><span>Issues</span></nav>
      <div class="is4-top">
        <div class="page-h"><h1>Issues</h1><span class="page-sub">anyone can see them · the Owner and Directors close them</span></div>
        <button type="button" class="btn primary" data-report>Report an issue</button>
      </div>
      <div id="iss-embed" class="is4-body"></div>
      ${canResolve(session) ? '<section class="is4-probs" id="iss-probs" aria-labelledby="iss-probs-h"></section>' : ''}
    </div>`;
  mount.querySelector('[data-crumb]').addEventListener('click', () => onGo && onGo('help'));
  mount.querySelector('[data-report]').addEventListener('click', report);
  await renderIssuesInto(mount.querySelector('#iss-embed'), session);
  const probs = mount.querySelector('#iss-probs');
  if (probs) renderProblemsInto(probs);
}

// What was sent with the Issue button (23/10/2026), to the Owner and Track
// support: read-only here, with where Track support has got to.
const PROB_STATE = { new: ['Sent', 'open'], seen: ['Seen by Track support', 'seen'], done: ['Done', 'done'] };
async function renderProblemsInto(box) {
  box.innerHTML = `<h2 class="section-h" id="iss-probs-h">Sent with the Issue button</h2>
    <p class="is4-probs-sub">Each one went to you and to Track support, with the screen, build, device and any recent errors. Track support marks them seen and done.</p>
    <p class="muted">Loading…</p>`;
  let list;
  try { list = await (await import('../data/help.js')).listProblems(); }
  catch (e) { box.querySelector('.muted').outerHTML = `<div class="load-error"><div class="empty-title">Reports didn’t load</div><p>${escapeHtml(e.message || String(e))}</p></div>`; return; }
  if (!box.isConnected) return;
  const when = (iso) => (iso ? new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');
  const rows = list.map((p) => {
    const [label, cls] = PROB_STATE[p.state] || PROB_STATE.new;
    const meta = [p.kind === 'idea' ? 'Idea' : 'Bug', p.screen, p.build, when(p.at), String(p.byEmail || '').split('@')[0] || null, p.shotPath ? 'screenshot attached' : null, (p.errors || []).length ? `${p.errors.length} error${p.errors.length === 1 ? '' : 's'}` : null].filter(Boolean).join(' · ');
    return `<div class="is4-row${p.state === 'done' ? ' done' : ''}">
      <div class="is4-main"><div class="is4-msg">${escapeHtml(p.text || '')}</div>
        ${p.detail ? `<div class="is4-detail">${escapeHtml(p.detail)}</div>` : ''}
        <div class="is4-meta">${escapeHtml(meta)}</div></div>
      <span class="is4-state ${cls}">${escapeHtml(label)}</span></div>`;
  }).join('');
  box.querySelector('.muted').outerHTML = list.length ? `<div class="is4-list arrive">${rows}</div>` : '<p class="is4-probs-sub">Nothing sent yet. The Issue button is at the foot of the menu.</p>';
}

/**
 * The list, into any container. Open to everyone; resolve controls and the
 * reporter's details (who, browser, screenshot) only for Owner and Director.
 * Re-render safe, and a render that has been superseded stands down.
 */
export async function renderIssuesInto(container, session, { tab: startTab = 'open' } = {}) {
  if (!container) return;
  const resolver = canResolve(session);
  container.innerHTML = '<p class="muted">Loading…</p>';

  let items;
  try { items = await listIssues(); }
  catch (e) { container.innerHTML = `<div class="load-error"><div class="empty-title">Issues didn’t load</div><p>${escapeHtml(e.message || String(e))}</p></div>`; return; }
  if (!container.isConnected) return;

  const byNewest = (a, b) => String(b.at || '').localeCompare(String(a.at || ''));
  const lists = { open: items.filter((i) => !i.resolved).sort(byNewest), resolved: items.filter((i) => i.resolved).sort(byNewest) };
  let tab = startTab;

  const draw = () => {
    const rows = lists[tab];
    container.innerHTML = `
      <div class="seg" role="tablist" aria-label="Issues">
        <button type="button" role="tab" data-tab="open" class="${tab === 'open' ? 'active' : ''}" aria-selected="${tab === 'open'}">Open ${lists.open.length}</button>
        <button type="button" role="tab" data-tab="resolved" class="${tab === 'resolved' ? 'active' : ''}" aria-selected="${tab === 'resolved'}">Resolved ${lists.resolved.length}</button>
      </div>
      ${rows.length ? `<div class="is4-list arrive">${rows.map((i) => row(i, resolver)).join('')}</div>` : tab === 'open' && !items.length ? `
      <div class="is4-empty">
        <span class="is4-empty-ico">${CHECK}</span>
        <h2>No issues reported</h2>
        <p>${resolver ? 'Reports from before the Issue button stay here. New ones are below, under Sent with the Issue button.' : 'Report an issue (or the Issue button at the foot of the menu) sends a screenshot and a line to your Owner and Track support.'}</p>
        <button type="button" class="btn" data-report>Report an issue</button>
      </div>` : `
      <div class="is4-empty">
        <span class="is4-empty-ico">${CHECK}</span>
        <h2>${tab === 'open' ? 'No open issues' : 'Nothing resolved yet'}</h2>
        <p>${tab === 'open' ? 'Everything reported has been resolved.' : 'Resolved issues move here, with who closed them.'}</p>
      </div>`}`;
    container.querySelectorAll('[data-tab]').forEach((b) => b.addEventListener('click', () => { tab = b.dataset.tab; draw(); }));
    container.querySelector('[data-report]')?.addEventListener('click', report);
    if (!resolver) return;
    // Screenshots and resolve controls are resolver-only (they carry reporter data).
    container.querySelectorAll('.issue-shot').forEach((img) => img.addEventListener('click', () => lightbox(img.src)));
    container.querySelectorAll('.issue-resolve').forEach((btn) => btn.addEventListener('click', async () => {
      // Two presses, as Home's "Sign and issue": the first arms, the second closes it.
      if (!btn.dataset.armed) {
        btn.dataset.armed = '1'; btn.textContent = 'Press again to resolve';
        setTimeout(() => { if (btn.isConnected && !btn.disabled) { delete btn.dataset.armed; btn.textContent = 'Mark resolved'; } }, 3000);
        return;
      }
      btn.disabled = true; btn.textContent = 'Resolving…';
      try {
        await updateIssue(btn.dataset.id, { resolved: true, resolvedAt: new Date().toISOString(), resolvedBy: session ? session.email : null });
        renderIssuesInto(container, session, { tab });
      } catch (e) { btn.disabled = false; btn.textContent = 'Mark resolved'; alert(e.message || e); }
    }));
    container.querySelectorAll('.issue-reopen').forEach((btn) => btn.addEventListener('click', async () => {
      btn.disabled = true;
      try { await updateIssue(btn.dataset.id, { resolved: false }); renderIssuesInto(container, session, { tab }); }
      catch (e) { btn.disabled = false; alert(e.message || e); }
    }));
  };
  draw();
}

function row(i, resolver) {
  const who = String(i.email || '').split('@')[0] || 'someone';
  const when = i.at ? new Date(i.at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';
  // Only render an <img> for a real image data URL (user-writable field), and
  // only to resolvers — a screenshot can contain another screen's content.
  const shot = resolver && typeof i.shot === 'string' && /^data:image\//.test(i.shot) ? i.shot : null;
  const resolved = !!i.resolved;
  const meta = [i.screen || null, i.build || null, when || null, resolver ? who : null].filter(Boolean).join(' · ');
  return `<div class="is4-row${resolved ? ' done' : ''}">
    <div class="is4-main">
      <div class="is4-msg">${escapeHtml(i.message || '(no description)')}</div>
      <div class="is4-meta">${escapeHtml(meta)}${resolved && i.resolvedBy && resolver ? ` · closed by ${escapeHtml(String(i.resolvedBy).split('@')[0])}` : ''}</div>
      ${resolver && i.ua ? `<div class="is4-ua">${escapeHtml(i.ua)}</div>` : ''}
      ${shot ? `<img class="issue-shot is4-shot" src="${escapeHtml(shot)}" alt="Screenshot attached to the report" loading="lazy">` : ''}
    </div>
    <span class="is4-state ${resolved ? 'done' : 'open'}">${resolved ? 'Resolved' : 'Open'}</span>
    ${resolver ? (resolved
      ? `<button class="btn sm issue-reopen" type="button" data-id="${escapeHtml(i.id)}">Reopen</button>`
      : `<button class="btn sm primary issue-resolve" type="button" data-id="${escapeHtml(i.id)}">Mark resolved</button>`) : ''}
  </div>`;
}

function lightbox(src) {
  const ov = document.createElement('div');
  ov.className = 'issue-lightbox';
  ov.innerHTML = `<img src="${escapeHtml(src)}" alt="screenshot">`;
  ov.addEventListener('click', () => ov.remove());
  document.body.appendChild(ov);
}
